import type { ActivityEntry, ConversationMessage, Note, Preferences } from "../types";
import { supabase } from "./supabase";

/** Legacy key from the offline demo mode; only cleaned up on sign-out. */
const NOTES_KEY = "parlenote:demo-notes";
const PREFERENCES_KEY = "parlenote:preferences";
/** Messages kept in memory and rehydrated at sign-in. */
export const CONVERSATION_WINDOW = 80;

const defaultPreferences: Preferences = {
  note: "always_ask",
  google_docs: "always_ask",
  calendar: "always_ask",
  googleDocsEnabled: true,
  calendarEnabled: true,
  autoSpeak: true,
  autoStopOnSilence: true,
  historyDays: 30
};

const PERMISSION_MODES: Preferences["note"][] = ["always_ask", "guarded_auto", "never"];
const HISTORY_CHOICES: Preferences["historyDays"][] = [7, 30, 90];

function mode(value: unknown, fallback: Preferences["note"]): Preferences["note"] {
  return PERMISSION_MODES.includes(value as Preferences["note"])
    ? (value as Preferences["note"])
    : fallback;
}

function flag(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

/**
 * The cache is user-editable storage, so every field is re-checked. A stale or
 * tampered entry must never be able to promote a destination to automatic.
 */
function sanitizePreferences(value: unknown): Preferences {
  const raw = (value && typeof value === "object" ? value : {}) as Record<string, unknown>;
  const days = Number(raw.historyDays);
  return {
    note: mode(raw.note, defaultPreferences.note),
    google_docs: mode(raw.google_docs, defaultPreferences.google_docs),
    calendar: mode(raw.calendar, defaultPreferences.calendar),
    googleDocsEnabled: flag(raw.googleDocsEnabled, defaultPreferences.googleDocsEnabled),
    calendarEnabled: flag(raw.calendarEnabled, defaultPreferences.calendarEnabled),
    autoSpeak: flag(raw.autoSpeak, defaultPreferences.autoSpeak),
    autoStopOnSilence: flag(raw.autoStopOnSilence, defaultPreferences.autoStopOnSilence),
    historyDays: HISTORY_CHOICES.includes(days as Preferences["historyDays"])
      ? (days as Preferences["historyDays"])
      : defaultPreferences.historyDays
  };
}

export function loadPreferences(): Preferences {
  try {
    return sanitizePreferences(JSON.parse(localStorage.getItem(PREFERENCES_KEY) || "{}"));
  } catch {
    return { ...defaultPreferences };
  }
}

/** Drops every device-local trace of the signed-out account. */
export function clearLocalPreferences(): void {
  localStorage.removeItem(PREFERENCES_KEY);
  localStorage.removeItem(NOTES_KEY);
}

export async function savePreferences(preferences: Preferences, userId?: string): Promise<void> {
  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  if (supabase && userId) {
    const { error } = await supabase.from("user_preferences").upsert({
      user_id: userId,
      note_permission: preferences.note,
      google_docs_permission: preferences.google_docs,
      calendar_permission: preferences.calendar,
      google_docs_enabled: preferences.googleDocsEnabled,
      calendar_enabled: preferences.calendarEnabled,
      auto_speak: preferences.autoSpeak,
      auto_stop_silence: preferences.autoStopOnSilence,
      history_days: preferences.historyDays,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    if (error) throw error;
  }
}

export async function loadPreferencesForUser(userId?: string): Promise<Preferences> {
  if (!supabase || !userId) return loadPreferences();
  const { data, error } = await supabase
    .from("user_preferences")
    .select(
      "note_permission,google_docs_permission,calendar_permission,google_docs_enabled,calendar_enabled,auto_speak,auto_stop_silence,history_days"
    )
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return loadPreferences();
  const preferences = sanitizePreferences({
    note: data.note_permission,
    google_docs: data.google_docs_permission,
    calendar: data.calendar_permission,
    googleDocsEnabled: data.google_docs_enabled,
    calendarEnabled: data.calendar_enabled,
    autoSpeak: data.auto_speak,
    autoStopOnSilence: data.auto_stop_silence,
    historyDays: data.history_days
  });
  localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  return preferences;
}

const NOTE_COLUMNS = "id,title,body,source_transcript,created_at,updated_at";

interface NoteRow {
  id: string;
  title: string;
  body: string;
  source_transcript: string | null;
  created_at: string;
  updated_at: string | null;
}

function mapNote(row: NoteRow): Note {
  return {
    id: row.id,
    title: row.title,
    body: row.body,
    sourceTranscript: row.source_transcript || undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at || undefined
  };
}

/**
 * Row Level Security is the real boundary, but every statement below also
 * carries `user_id` so a mistyped identifier fails loudly instead of silently
 * widening the query.
 */
function notesTable() {
  if (!supabase) throw new Error("Connectez-vous pour accéder à vos notes.");
  return supabase.from("notes");
}

export async function listNotes(userId?: string): Promise<Note[]> {
  if (!supabase || !userId) throw new Error("Connectez-vous pour consulter vos notes.");
  const { data, error } = await notesTable()
    .select(NOTE_COLUMNS)
    .eq("user_id", userId)
    .order("updated_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data || []).map(mapNote);
}

/**
 * Reads a single note by identifier. The agent searches the database directly,
 * so it can legitimately return a note that is not in the locally loaded page.
 */
export async function getNote(noteId: string, userId?: string): Promise<Note | undefined> {
  if (!supabase || !userId) return undefined;
  const { data, error } = await notesTable()
    .select(NOTE_COLUMNS)
    .eq("id", noteId)
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ? mapNote(data) : undefined;
}

export async function createNote(
  input: Pick<Note, "title" | "body" | "sourceTranscript">,
  userId?: string
): Promise<Note> {
  if (!supabase || !userId) throw new Error("Connectez-vous pour créer une note.");
  const { data, error } = await notesTable()
    .insert({
      user_id: userId,
      title: input.title,
      body: input.body,
      source_transcript: input.sourceTranscript
    })
    .select(NOTE_COLUMNS)
    .single();
  if (error) throw error;
  return mapNote(data);
}

export async function updateNote(
  noteId: string,
  input: Pick<Note, "title" | "body">,
  userId?: string
): Promise<void> {
  if (!supabase || !userId) throw new Error("Connectez-vous pour modifier une note.");
  const { error } = await notesTable()
    .update({ title: input.title, body: input.body })
    .eq("id", noteId)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function deleteNote(noteId: string, userId?: string): Promise<void> {
  if (!supabase || !userId) throw new Error("Connectez-vous pour supprimer une note.");
  const { error } = await notesTable().delete().eq("id", noteId).eq("user_id", userId);
  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Activity                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Records what ParleNote created outside itself.
 *
 * The link to a fresh Google document only existed in a toast, so it vanished a
 * few seconds later with no way to find it again. Failing to log must never
 * fail the creation that already succeeded.
 */
export async function recordActivity(
  userId: string | undefined,
  entry: {
    destination: "note" | "google_docs" | "calendar";
    title: string;
    externalId?: string;
    externalUrl?: string;
  }
): Promise<void> {
  if (!supabase || !userId) return;
  await supabase.from("activity").insert({
    user_id: userId,
    destination: entry.destination,
    title: entry.title.slice(0, 160),
    status: "created",
    external_id: entry.externalId,
    external_url: entry.externalUrl
  });
}

export async function listActivity(userId?: string, limit = 8): Promise<ActivityEntry[]> {
  if (!supabase || !userId) return [];
  const { data, error } = await supabase
    .from("activity")
    .select("id,destination,title,external_url,created_at")
    .eq("user_id", userId)
    .not("external_url", "is", null)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []).map((row) => ({
    id: row.id,
    destination: row.destination,
    title: row.title,
    url: row.external_url,
    createdAt: row.created_at
  }));
}

function mapMessage(message: {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}): ConversationMessage {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: message.created_at
  };
}

export async function loadConversationMessages(
  userId: string,
  historyDays: number
): Promise<ConversationMessage[]> {
  if (!supabase) return [];
  const since = new Date(Date.now() - historyDays * 24 * 60 * 60 * 1000).toISOString();
  // Take the newest slice, then restore chronological order. Reading ascending
  // with a limit returned the *oldest* messages, so a long-running account lost
  // its recent context entirely.
  const { data, error } = await supabase
    .from("conversation_messages")
    .select("id,role,content,created_at")
    .eq("user_id", userId)
    .gte("created_at", since)
    .order("created_at", { ascending: false })
    .limit(CONVERSATION_WINDOW);
  if (error) throw error;
  return (data || []).map(mapMessage).reverse();
}

export async function appendConversationMessage(
  userId: string,
  role: "user" | "assistant",
  content: string
): Promise<ConversationMessage> {
  if (!supabase) throw new Error("La conversation nécessite une session active.");
  const { data, error } = await supabase
    .from("conversation_messages")
    .insert({ user_id: userId, role, content: content.slice(0, 50_000) })
    .select("id,role,content,created_at")
    .single();
  if (error) throw error;
  return mapMessage(data);
}

/**
 * Enforces the retention preference on the server. Filtering on read only hid
 * older messages; they now actually leave the table. Failure is non-fatal —
 * housekeeping must never block sign-in.
 */
export async function pruneConversationMessages(historyDays: number): Promise<void> {
  if (!supabase) return;
  await supabase.rpc("prune_conversation_messages", { keep_days: historyDays });
}

/**
 * Rewrites a message in place, used to replace the raw dictation with the
 * cleaned-up sentence once the server has produced it.
 */
export async function updateConversationMessage(
  messageId: string,
  content: string,
  userId: string
): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from("conversation_messages")
    .update({ content: content.slice(0, 50_000) })
    .eq("id", messageId)
    .eq("user_id", userId);
  if (error) throw error;
}

export async function clearConversationMessages(userId: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from("conversation_messages")
    .delete()
    .eq("user_id", userId);
  if (error) throw error;
}
